import React, { useCallback } from "react";
import { useRecoilValue } from "recoil";
import { PortalFeedbackModal, SubmitUserFeedback } from "@bto/shared";
import type { FeedbackSource } from "@bto/shared";
import { useFeedbackForm } from "@bto/shared/hooks/useFeedbackForm";
import { providerProfileState } from "../../recoil/atoms";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { getBaseUrl } from "../../utils/requestUtils";
import { HELP_PANEL_WIDTH_PX } from "../../pages/Help/Help.constants";

const FEEDBACK_CANCEL_TOAST_MESSAGE =
  "You can always access the feedback tool in the right end of the top bar.";
const FEEDBACK_MODAL_PANEL_GAP_PX = 24;

interface ProviderFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: FeedbackSource;
  isHelpPanelOpen?: boolean;
}

export const ProviderFeedbackModal: React.FC<ProviderFeedbackModalProps> = ({
  isOpen,
  onClose,
  source,
  isHelpPanelOpen = false,
}) => {
  const { deviceType } = useDeviceHook();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const providerProfile = useRecoilValue(providerProfileState);

  const submit = useCallback(
    async (score: number, comment: string) => {
      if (!auth?.token) return new Response(null, { status: 401 });
      const response = await SubmitUserFeedback(
        { score, ...(comment && { comment }), source },
        await getBaseUrl(),
        auth.token,
      );
      if (response.status === 401) setLoggedOut(true);
      return response;
    },
    [auth?.token, source, setLoggedOut],
  );

  const {
    score,
    setScore,
    comment,
    setComment,
    isSubmitting,
    isSubmitted,
    error,
    handleSubmit,
    handleClose,
  } = useFeedbackForm({ submit, onClose });

  return (
    <PortalFeedbackModal
      isOpen={isOpen}
      firstName={providerProfile?.profile?.first_name}
      score={score}
      comment={comment}
      isSubmitting={isSubmitting}
      isSubmitted={isSubmitted}
      error={error}
      onScoreChange={setScore}
      onCommentChange={setComment}
      onSubmit={handleSubmit}
      onClose={handleClose}
      variant={deviceType === "desktop" ? "desktop" : "mobile"}
      desktopRightOffset={
        isHelpPanelOpen
          ? `${HELP_PANEL_WIDTH_PX + FEEDBACK_MODAL_PANEL_GAP_PX}px`
          : undefined
      }
      introQuestion="How likely are you to recommend the Unity Portal to a colleague or peer?"
      cancelToastMessage={FEEDBACK_CANCEL_TOAST_MESSAGE}
    />
  );
};
